import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from "@mui/material";

/**
 * A controlled select field component without react-hook-form dependencies.
 * Supports single or multiple selection, disabling options, and displays error/helper text.
 */
export interface ControlledSelectFieldProps {
  /**
   * The name attribute for the select element.
   */
  name?: string;
  /**
   * The current selected value(s).
   */
  value: string | string[];
  /**
   * Callback invoked when the selected value changes.
   */
  onChange: (value: string | string[]) => void;
  /**
   * Options to display. Can be an array of strings or objects with label/value.
   */
  options: Array<string | { label: string; value: string }>;
  /**
   * Whether multiple selection is allowed.
   */
  multiple?: boolean;
  /**
   * Array of option values that should be disabled.
   */
  disabledOptions?: string[];
  /**
   * Label to display above the select.
   */
  label?: string;
  /**
   * If true, the select will be styled as having an error.
   */
  error?: boolean;
  /**
   * Helper text to show beneath the select (e.g. validation message).
   */
  helperText?: string;
}

export function ControlledSelectField({
  name,
  value,
  onChange,
  options,
  multiple = false,
  disabledOptions = [],
  label,
  error = false,
  helperText,
}: Readonly<ControlledSelectFieldProps>) {
  const theme = useTheme();
  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const newValue = event.target.value;
    onChange(newValue);
  };

  return (
    <FormControl fullWidth error={error}>
      {label && (
        <Typography
          variant="body2"
          sx={{ marginBottom: "4px", fontWeight: 500 }}
        >
          {label}
        </Typography>
      )}
      <Select
        labelId={label ? `${name}-label` : undefined}
        name={name}
        value={value}
        onChange={handleChange}
        multiple={multiple}
        displayEmpty
        fullWidth
        slotProps={{
          input: {
            style: {
              borderRadius: "6px",
              height: "45px",
            },
          },
        }}
        sx={{
          "& input": {
            fontSize: "14px",
          },
          "& .MuiInputLabel-root": {
            fontSize: "14px",
          },
          "& .MuiSelect-select": {
            whiteSpace: "normal",
            textOverflow: "clip",
            overflowWrap: "anywhere",
            minHeight: "45px",
            display: "flex",
            alignItems: "center",
          },
          fontSize: "14px",
          height: "45px",
          fieldset: {
            borderRadius: "6px",
            borderColor: error
              ? "#d32f2f !important"
              : theme.palette.text.primary,
          },
        }}
      >
        {options?.map((opt) => {
          const val = typeof opt === "string" ? opt : opt.value;
          const lab = typeof opt === "string" ? opt : opt.label;
          return (
            <MenuItem
              key={val}
              value={val}
              disabled={disabledOptions.includes(val)}
              sx={{
                fontSize: "14px",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            >
              {lab}
            </MenuItem>
          );
        })}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
